'use client';

/**
 * ChatterPostSection - Internal note compose for Chat tab
 *
 * Renders a simple text area and submit button for posting internal notes
 * to Odoo chatter. No external delivery (WhatsApp/email) - just chatter.
 *
 * Schema-driven: rendered when CHAT_SCHEMA includes type='chatter_post'.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot
 */

import { useState } from 'react';
import { Send, MessageSquarePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface ChatterPostSectionProps {
  onPost?: (message: string) => Promise<{ success: boolean; message_id?: number; error?: string }>;
  isPosting?: boolean;
}

export function ChatterPostSection({
  onPost,
  isPosting = false,
}: ChatterPostSectionProps) {
  const [message, setMessage] = useState('');

  const handlePost = async () => {
    if (!message.trim() || !onPost) return;

    const result = await onPost(message.trim());
    if (result.success) {
      const desc = result.message_id
        ? `Message #${result.message_id} posted to timeline`
        : 'Your note has been posted to the timeline';
      toast.success('Note added', { description: desc });
      setMessage('');
    } else {
      toast.error('Failed to post note', { description: result.error || 'Unknown error' });
    }
  };

  const canPost = !!message.trim() && !isPosting && !!onPost;

  return (
    <div className="p-3 space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <MessageSquarePlus className="h-3.5 w-3.5" />
        <span className="font-medium">Add Internal Note</span>
      </div>

      {/* Text area */}
      <Textarea
        placeholder="Add a note to the timeline..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={2}
        className="resize-none text-sm"
        disabled={isPosting}
      />

      {/* Send button */}
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={handlePost}
          disabled={!canPost}
          className="gap-1.5"
        >
          {isPosting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          Post Note
        </Button>
      </div>
    </div>
  );
}
